import axiosInstance from '../config/axios';
import {
  PaymentReport,
  PaymentReportFormValues,
  PaymentReportListFilters,
  PaymentReportListResponse,
  PaymentReportAccess,
  PaymentReportHistory,
  PaymentReportComment,
  PaymentReportDocument,
  PaymentSummary,
  PaymentHistoryItem,
  ProjectOption,
  PartyOption,
  POOption,
  InvoiceOption,
  WorkflowAssignee,
  DocumentType,
  BeneficiaryBudgetInfo,
} from '../types/paymentReport';

const BASE_URL = '/api/reports';

export const paymentReportService = {
  /**
   * Get current user's payment report access
   */
  getAccess: async (): Promise<{ success: boolean; data: PaymentReportAccess }> => {
    const response = await axiosInstance.get(`${BASE_URL}/access`);
    return response.data;
  },

  /**
   * Get projects for dropdown
   */
  getProjects: async (search?: string): Promise<{ success: boolean; data: ProjectOption[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/projects`, {
      params: { search },
    });
    return response.data;
  },

  /**
   * Get parties (vendors) for dropdown - filtered by project
   */
  getParties: async (
    projectId: string,
    search?: string
  ): Promise<{ success: boolean; data: PartyOption[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/projects/${projectId}/parties`, {
      params: { search },
    });
    return response.data;
  },

  /**
   * Get POs for dropdown - filtered by project and party
   */
  getPOs: async (
    projectId: string,
    partyId: string
  ): Promise<{ success: boolean; data: POOption[] }> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/projects/${projectId}/parties/${partyId}/pos`
    );
    return response.data;
  },

  /**
   * Get invoices for dropdown - filtered by PO
   */
  getInvoices: async (poId: number): Promise<{ success: boolean; data: InvoiceOption[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/pos/${poId}/invoices`);
    return response.data;
  },

  /**
   * Get invoice details for auto-population
   */
  getInvoiceDetails: async (invoiceId: number): Promise<{ success: boolean; data: any }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${invoiceId}/details`);
    return response.data;
  },

  /**
   * Get payment summary for a PO
   */
  getPaymentSummary: async (
    poId: number,
    invoiceAmount: number,
    excludeReportId?: number
  ): Promise<{ success: boolean; data: PaymentSummary }> => {
    const response = await axiosInstance.get(`${BASE_URL}/pos/${poId}/payment-summary`, {
      params: { invoiceAmount, excludeReportId },
    });
    return response.data;
  },

  /**
   * Get payment history for a PO
   */
  getPaymentHistory: async (poId: number): Promise<{ success: boolean; data: PaymentHistoryItem[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/pos/${poId}/payment-history`);
    return response.data;
  },

  /**
   * Get beneficiary party payment budget info for a PO
   */
  getBeneficiaryBudget: async (
    poId: number,
    excludeReportId?: number
  ): Promise<{ success: boolean; data: BeneficiaryBudgetInfo }> => {
    const response = await axiosInstance.get(`${BASE_URL}/pos/${poId}/beneficiary-budget`, {
      params: { excludeReportId },
    });
    return response.data;
  },

  /**
   * Get workflow assignees for a role
   */
  getWorkflowAssignees: async (
    role: 'reviewer' | 'reviewer_2' | 'recommender' | 'sanctioner'
  ): Promise<{ success: boolean; data: WorkflowAssignee[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/workflow-assignees/${role}`);
    return response.data;
  },

  /**
   * Get default sanctioning authorities
   */
  getDefaultSanctioners: async (): Promise<{
    success: boolean;
    data: { level1: WorkflowAssignee | null; level2: WorkflowAssignee | null };
  }> => {
    const response = await axiosInstance.get(`${BASE_URL}/default-sanctioners`);
    return response.data;
  },

  /**
   * Create a new payment report
   */
  createPaymentReport: async (
    payload: PaymentReportFormValues
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports`, payload);
    return response.data;
  },

  /**
   * Create and submit payment report with documents in one API call
   */
  createAndSubmitPaymentReport: async (
    payload: PaymentReportFormValues,
    measurementSheetFiles: File[],
    workCompletionFiles: File[],
    supportingDocFiles: File[] = []
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const formData = new FormData();

    // Add form data as JSON
    formData.append('data', JSON.stringify(payload));

    // Add measurement sheet files
    measurementSheetFiles.forEach((file) => {
      formData.append('measurementSheets', file);
    });

    // Add work completion certificate files
    workCompletionFiles.forEach((file) => {
      formData.append('workCompletionCertificates', file);
    });

    // Add supporting document files
    supportingDocFiles.forEach((file) => {
      formData.append('supportingDocuments', file);
    });

    const response = await axiosInstance.post(
      `${BASE_URL}/payment-reports/create-and-submit`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Get payment report by ID
   */
  getPaymentReport: async (id: number): Promise<{ success: boolean; data: PaymentReport }> => {
    const response = await axiosInstance.get(`${BASE_URL}/payment-reports/${id}`);
    return response.data;
  },

  /**
   * Export payment report as PDF
   */
  exportPaymentReportPdf: async (id: number) => {
    return axiosInstance.get(`${BASE_URL}/payment-reports/${id}/pdf`, {
      responseType: 'blob',
    });
  },

  /**
   * Export payment report as PDF with enclosed documents
   */
  exportPaymentReportPdfWithDocuments: async (id: number) => {
    return axiosInstance.get(`${BASE_URL}/payment-reports/${id}/pdf-with-documents`, {
      responseType: 'blob',
    });
  },


  /**
   * List payment reports with filters
   */
  listPaymentReports: async (
    filters?: PaymentReportListFilters
  ): Promise<PaymentReportListResponse & { success: boolean }> => {
    const response = await axiosInstance.get(`${BASE_URL}/payment-reports`, { params: filters });
    return response.data;
  },

  /**
   * Update payment report
   */
  updatePaymentReport: async (
    id: number,
    payload: Partial<PaymentReportFormValues>
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.put(`${BASE_URL}/payment-reports/${id}`, payload);
    return response.data;
  },

  /**
   * Delete payment report
   */
  deletePaymentReport: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`${BASE_URL}/payment-reports/${id}`);
    return response.data;
  },

  /**
   * Submit payment report for review
   */
  submitPaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/submit`, {
      comments,
    });
    return response.data;
  },

  /**
   * Review payment report
   */
  reviewPaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/review`, {
      comments,
    });
    return response.data;
  },

  /**
   * Second review payment report
   */
  reviewLevel2PaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/review-2`, {
      comments,
    });
    return response.data;
  },

  /**
   * Recommend payment report
   */
  recommendPaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/recommend`, {
      comments,
    });
    return response.data;
  },

  /**
   * Sanction payment report (Level 1)
   */
  sanctionLevel1PaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/sanction-1`, {
      comments,
    });
    return response.data;
  },

  /**
   * Sanction payment report (Level 2)
   */
  sanctionLevel2PaymentReport: async (
    id: number,
    comments?: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/sanction-2`, {
      comments,
    });
    return response.data;
  },

  /**
   * Reject payment report
   */
  rejectPaymentReport: async (
    id: number,
    reason: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/reject`, {
      reason,
    });
    return response.data;
  },

  /**
   * Return payment report for correction
   */
  returnForCorrection: async (
    id: number,
    comments: string
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/return`, {
      comments,
    });
    return response.data;
  },

  /**
   * Get workflow history
   */
  getWorkflowHistory: async (
    id: number
  ): Promise<{ success: boolean; data: PaymentReportHistory[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/payment-reports/${id}/workflow-history`);
    return response.data;
  },

  /**
   * Add comment
   */
  addComment: async (
    id: number,
    comment: string,
    commentType?: 'general' | 'review' | 'recommendation' | 'sanction'
  ): Promise<{ success: boolean; data: PaymentReportComment; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/payment-reports/${id}/comments`, {
      comment,
      commentType: commentType || 'general',
    });
    return response.data;
  },

  /**
   * Get comments
   */
  getComments: async (id: number): Promise<{ success: boolean; data: PaymentReportComment[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/payment-reports/${id}/comments`);
    return response.data;
  },

  // ============================================================
  // Document Upload Methods
  // ============================================================

  /**
   * Upload document for payment report
   */
  uploadDocument: async (
    reportId: number,
    file: File,
    documentType: DocumentType
  ): Promise<{ success: boolean; data: PaymentReportDocument; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const response = await axiosInstance.post(
      `${BASE_URL}/payment-reports/${reportId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Get documents for payment report
   */
  getDocuments: async (
    reportId: number,
    documentType?: DocumentType
  ): Promise<{ success: boolean; data: PaymentReportDocument[] }> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/payment-reports/${reportId}/documents`,
      {
        params: { documentType },
      }
    );
    return response.data;
  },

  /**
   * Delete document from payment report
   */
  deleteDocument: async (
    reportId: number,
    documentId: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(
      `${BASE_URL}/payment-reports/${reportId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Get document download URL
   */
  getDocumentUrl: (reportId: number, documentId: number): string => {
    return `${BASE_URL}/payment-reports/${reportId}/documents/${documentId}/download`;
  },

  /**
   * Fetch document as blob for preview/download
   */
  fetchDocument: async (reportId: number, documentId: number) => {
    return axiosInstance.get(`${BASE_URL}/payment-reports/${reportId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
  },

  /**
   * Mark payment as completed (RTGS/Proof)
   */
  completePayment: async (
    reportId: number,
    rtgsNumber?: string,
    paymentProof?: File | null
  ): Promise<{ success: boolean; data: PaymentReport; message: string }> => {
    const formData = new FormData();
    if (rtgsNumber) {
      formData.append('rtgsNumber', rtgsNumber);
    }
    if (paymentProof) {
      formData.append('paymentProof', paymentProof);
    }

    const response = await axiosInstance.post(
      `${BASE_URL}/payment-reports/${reportId}/payment-complete`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

export default paymentReportService;
